import React, { useState, useEffect } from 'react';
import { Contact } from '../../../types/Contact';
import { User } from '@supabase/supabase-js';
import NotificationToast from '../NotificationToast';
import { useNotifications } from '../../hooks/useNotifications';
import ShareModal from '../ShareModal';
import CollectionSelectorModal from '../CollectionSelectorModal';
import ContactGalleryModal from '../ContactGalleryModal';
import TranscriptView from '../TranscriptView';
import { uploadCompressedImage } from '../../lib/imageUpload';
import { createEvent } from '../../lib/events';
import { getClaimedContactIdCore } from '@gv/core';
import { supabase } from '../../lib/supabase';

// Import the extracted components
import ContactHeader from './sections/ContactHeader';
import ContactProfile from './sections/ContactProfile';
import OverviewSection from './sections/OverviewSection';
import BasicInfoSection from './sections/BasicInfoSection';
import PersonalInfoSection from './sections/PersonalInfoSection';
import ProfessionalSection from './sections/ProfessionalSection';
import RelationshipsSection from './sections/RelationshipsSection';
import EventsSection from './sections/EventsSection';
import ContactSettings from './sections/ContactSettings';

interface ContactDetailPageProps {
    contact: Contact;
    user: User | null;
    personalProfile?: string;
    collectionContext?: string;
    collectionName?: string;
    collectionId?: string;
    onBack: () => void;
    onSave: () => void;
}

export default function ContactDetailPage({
    contact,
    user,
    personalProfile,
    collectionContext,
    collectionName,
    collectionId,
    onBack,
    onSave
}: ContactDetailPageProps) {
    const [formData, setFormData] = useState<Contact>(contact);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
    // Header search state
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResponse, setSearchResponse] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    // Add Details state controlled via global events
    const [addDetailsOpen, setAddDetailsOpenState] = useState(false);
    // Add Photo modal state
    const [isAddPhotoOpen, setIsAddPhotoOpen] = useState(false);
    const [addPhotoUrl, setAddPhotoUrl] = useState<string | null>(null);
    const [addPhotoDesc, setAddPhotoDesc] = useState('');
    const [addPhotoWhen, setAddPhotoWhen] = useState<string>(() => new Date().toISOString().slice(0, 10));
    const photoFileRef = React.useRef<HTMLInputElement | null>(null);
    const [addPhotoSaving, setAddPhotoSaving] = useState(false);
    // Transcript chat state
    const [transcriptChatOpen, setTranscriptChatOpen] = useState(false);
    const [transcriptEvent, setTranscriptEvent] = useState<any>(null);

    const { notification, showNotification, hideNotification } = useNotifications();

    useEffect(() => {
        setFormData(contact);
    }, [contact]);

    // On contact switch: either restore a saved position (when returning from a related profile)
    // or scroll to top normally
    useEffect(() => {
        try {
            const pending = sessionStorage.getItem('gv:return:pending');
            const retContact = sessionStorage.getItem('gv:return:contactId');
            const scroll = sessionStorage.getItem('gv:return:scroll');
            const relId = sessionStorage.getItem('gv:return:relationshipId');
            const isForThisContact = pending === '1' && !!retContact && retContact === formData.id;
            if (isForThisContact) {
                const container = document.getElementById('main-scroll-container');
                const top = scroll ? Number(scroll) : 0;
                if (container && typeof (container as any).scrollTo === 'function') {
                    try { (container as any).scrollTo({ top, behavior: 'instant' as any }); } catch { (container as any).scrollTop = top; }
                } else {
                    try { window.scrollTo({ top, behavior: 'instant' as any }); } catch { window.scrollTo(0, top); }
                }
                // Ask RelationshipsSection to open the previous relationship modal (slightly deferred to ensure listeners are ready)
                if (relId) {
                    setTimeout(() => {
                        try { window.dispatchEvent(new CustomEvent('gv:openRelationship' as any, { detail: { id: relId } } as any)); } catch { }
                    }, 50);
                }
                return;
            }
        } catch { /* ignore and fall through */ }

        // Default behavior when not restoring
        try {
            window.scrollTo({ top: 0, behavior: 'instant' as any });
        } catch {
            window.scrollTo(0, 0);
        }
    }, [formData.id]);

    const handleFieldChange = (field: keyof Contact, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Bridge for Add Details actions (listeners elsewhere can respond)
    const handleAddDetails = () => {
        try {
            const evt = addDetailsOpen ? 'gv:closeAddDetails' : 'gv:openAddDetails';
            window.dispatchEvent(new CustomEvent(evt, { detail: { contactId: formData.id } }));
            if (!addDetailsOpen) setSearchOpen(false);
        } catch {
            /* no-op */
        }
    };

    // Allow header to request opening/closing the Add Details panel
    const setAddDetailsOpen = (open: boolean) => {
        try {
            const evt = open ? 'gv:openAddDetails' : 'gv:closeAddDetails';
            window.dispatchEvent(new CustomEvent(evt, { detail: { contactId: formData.id } }));
        } catch {
            /* no-op */
        }
    };

    // Listen for Add Details open/close events and keep local state in sync
    useEffect(() => {
        const onOpen = (e: Event) => {
            setAddDetailsOpenState(true);
            // close search panel when opening add details for exclusivity
            setSearchOpen(false);
        };
        const onClose = (e: Event) => setAddDetailsOpenState(false);
        window.addEventListener('gv:openAddDetails', onOpen as any);
        window.addEventListener('gv:closeAddDetails', onClose as any);
        return () => {
            window.removeEventListener('gv:openAddDetails', onOpen as any);
            window.removeEventListener('gv:closeAddDetails', onClose as any);
        };
    }, []);

    // Simple placeholder search handler; wires up UI without backend dependency here
    const handleSearch = async () => {
        const q = searchQuery.trim();
        if (!q) return;
        setSearchLoading(true);
        try {
            // TODO: integrate AI/knowledge search; placeholder response for now
            setSearchResponse(`Search for "${q}" coming soon.`);
        } finally {
            setSearchLoading(false);
        }
    };

    return (
        <>
            <div className="min-h-screen bg-gray-900 text-white">
                {/* Contact Header with navigation and actions */}
                <ContactHeader
                    contact={formData}
                    user={user}
                    onBack={onBack}
                    searchOpen={searchOpen}
                    setSearchOpen={setSearchOpen}
                    setIsShareOpen={setIsShareOpen}
                    setIsCollectionModalOpen={setIsCollectionModalOpen}
                    onAddPhoto={() => setIsAddPhotoOpen(true)}
                    setAddDetailsOpen={setAddDetailsOpen}
                    addDetailsOpen={addDetailsOpen}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchResponse={searchResponse}
                    searchLoading={searchLoading}
                    handleSearch={handleSearch}
                >
                    {/* Contact Profile with photo, name, and quick contact */}
                    <ContactProfile
                        contact={formData}
                        setIsGalleryOpen={setIsGalleryOpen}
                        handleAddDetails={handleAddDetails}
                        showNotification={showNotification}
                    />
                </ContactHeader>

                {/* Main Content Container */}
                <div className="max-w-4xl mx-auto px-4 pb-8">
                    <div className="space-y-6">
                        {/* Overview Section with AI-generated TLDR */}
                        <OverviewSection
                            contactId={formData.id || ''}
                            userId={(user as any)?.id || ''}
                            formData={formData}
                            setFormData={setFormData}
                            onSave={onSave}
                            showNotification={showNotification}
                        />

                        {/* Basic Info Section */}
                        <BasicInfoSection
                            formData={formData}
                            setFormData={setFormData}
                            onSave={onSave}
                            showNotification={showNotification}
                        />

                        {/* Personal Info Section */}
                        <PersonalInfoSection
                            formData={formData}
                            setFormData={setFormData}
                            onSave={onSave}
                            showNotification={showNotification}
                        />

                        {/* Professional Section */}
                        <ProfessionalSection
                            contactId={formData.id || ''}
                            userId={(user as any)?.id || ''}
                        />

                        {/* Relationships Section */}
                        <RelationshipsSection
                            contactId={formData.id || ''}
                            userId={(user as any)?.id || ''}
                        />

                        {/* Events Section */}
                        <EventsSection
                            contactId={formData.id || ''}
                            userId={(user as any)?.id || ''}
                            onTranscriptClick={(event) => {
                                setTranscriptEvent(event);
                                setTranscriptChatOpen(true);
                            }}
                        />

                        {/* Contact Settings Section */}
                        <ContactSettings
                            contactId={formData.id || ''}
                            userId={(user as any)?.id || ''}
                            onBack={onBack}
                            showNotification={showNotification}
                        />
                    </div>
                </div>
            </div>

            {/* Modals */}
            {isShareOpen && (
                <ShareModal
                    formData={formData as any}
                    onClose={() => setIsShareOpen(false)}
                    onNotification={showNotification}
                />
            )}

            {isCollectionModalOpen && user && formData.id && (
                <CollectionSelectorModal
                    selectedContactIds={[formData.id]}
                    user={user as any}
                    onClose={() => setIsCollectionModalOpen(false)}
                    onContactsAdded={() => setIsCollectionModalOpen(false)}
                />
            )}

            {isGalleryOpen && (
                <ContactGalleryModal
                    contactId={formData.id || ''}
                    contactName={`${formData.first_name || ''} ${formData.last_name || ''}`.trim()}
                    defaultUrl={formData.profile_image_url || null}
                    isOpen={isGalleryOpen}
                    onClose={() => setIsGalleryOpen(false)}
                />
            )}

            {/* Transcript View Modal */}
            {transcriptChatOpen && transcriptEvent && (
                <TranscriptView
                    event={transcriptEvent}
                    isOpen={transcriptChatOpen}
                    onClose={() => {
                        setTranscriptChatOpen(false);
                        setTranscriptEvent(null);
                    }}
                    onContactSelect={(contactId: string) => {
                        // Navigate to contact profile
                        setTranscriptChatOpen(false);
                        setTranscriptEvent(null);
                        // You might want to add navigation to contact profile here
                        console.log('Navigate to contact:', contactId);
                    }}
                    currentUserId={user?.id}
                />
            )}

            {/* Add Photo via Event Modal */}
            {isAddPhotoOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" onClick={() => !addPhotoSaving && setIsAddPhotoOpen(false)} />
                    <div className="relative w-full sm:max-w-lg bg-gray-900 border border-gray-700 rounded-xl p-5 shadow-2xl">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-white">Add Photo</h3>
                            <button
                                onClick={() => !addPhotoSaving && setIsAddPhotoOpen(false)}
                                className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700"
                                aria-label="Close"
                                title="Close"
                            >
                                ×
                            </button>
                        </div>

                        {/* Hidden file input */}
                        <input
                            ref={photoFileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (!formData.id) { showNotification({ type: 'error', message: 'Missing contact id.' }); return; }
                                try {
                                    const url = await uploadCompressedImage(file, `event-photos/${formData.id}`);
                                    setAddPhotoUrl(url);
                                } catch (err) {
                                    console.error('Upload failed', err);
                                    showNotification({ type: 'error', message: 'Failed to upload photo. Please try again.' });
                                }
                            }}
                        />

                        <div className="space-y-4">
                            <div className="flex items-center justify-center">
                                <button
                                    type="button"
                                    onClick={() => photoFileRef.current?.click()}
                                    className="w-32 h-32 rounded-lg bg-gray-900 border border-gray-700 relative overflow-hidden"
                                >
                                    {addPhotoUrl ? (
                                        <img src={addPhotoUrl} alt="selected" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="absolute inset-0 grid place-items-center text-gray-400">Select photo</div>
                                    )}
                                </button>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 mb-1">When</label>
                                <input
                                    type="date"
                                    value={addPhotoWhen}
                                    onChange={(e) => setAddPhotoWhen(e.target.value)}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 mb-1">What happened?</label>
                                <textarea
                                    value={addPhotoDesc}
                                    onChange={(e) => setAddPhotoDesc(e.target.value)}
                                    placeholder="Describe the photo (e.g., at the gym)"
                                    rows={4}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    disabled={addPhotoSaving}
                                    onClick={() => setIsAddPhotoOpen(false)}
                                    className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={addPhotoSaving || !addPhotoUrl}
                                    onClick={async () => {
                                        if (!formData.id) { showNotification({ type: 'error', message: 'Missing contact id.' }); return; }
                                        if (!addPhotoUrl) { showNotification({ type: 'error', message: 'Please select a photo first.' }); return; }
                                        try {
                                            setAddPhotoSaving(true);
                                            // Resolve my contact (sender)
                                            let myContactId: string | null = null;
                                            try { myContactId = await getClaimedContactIdCore(supabase as any, (user as any)?.id || ''); } catch { myContactId = null; }

                                            const contactName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Contact';
                                            const entry: any = {
                                                name: 'Photo',
                                                when: addPhotoWhen,
                                                notes: addPhotoDesc || '',
                                                photoUrl: addPhotoUrl,
                                                participants: [
                                                    { contactId: formData.id, name: contactName },
                                                    ...(myContactId ? [{ contactId: myContactId }] : []),
                                                ],
                                            };

                                            await createEvent(formData.id, entry);

                                            // Notify Events section to refresh
                                            try { window.dispatchEvent(new CustomEvent('gv:refreshEvents' as any, { detail: { contactId: formData.id } } as any)); } catch { }

                                            showNotification({ type: 'success', message: 'Photo added as an event.' });
                                            setIsAddPhotoOpen(false);
                                            setAddPhotoUrl(null);
                                            setAddPhotoDesc('');
                                            setAddPhotoWhen(new Date().toISOString().slice(0, 10));
                                        } catch (err) {
                                            console.error('Failed to create photo event', err);
                                            showNotification({ type: 'error', message: 'Failed to create event. Please try again.' });
                                        } finally {
                                            setAddPhotoSaving(false);
                                        }
                                    }}
                                    className="px-3 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                                >
                                    {addPhotoSaving ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notifications */}
            {notification && (
                <NotificationToast
                    notification={notification}
                    onClose={hideNotification}
                />
            )}
        </>
    );
}